import { useMemo, useEffect, useState } from 'react'
import { TemplateAttribute } from '@/services/hooks/template_attributes'
import { getAbsoluteYFromSubPage } from '../Annotations/utils'
import { useDocumentState } from '@/state'

type ElementWithPosition = {
  templateAttribute: TemplateAttribute<any>
  absoluteY: number
  estimatedHeight: number
}

const OVERSCAN_PIXELS = 1000 // Render elements 1000px above/below viewport
const DEFAULT_ELEMENT_HEIGHT = 100 // Default height estimate for elements

/**
 * Gets the height of an element, using actual stored dimensions when available,
 * falling back to type-based estimates
 */
const estimateElementHeight = (template: TemplateAttribute<any>): number => {
  const { data_type, template_values } = template

  /* Use actual stored height if available */
  if (template_values?.size?.height) {
    return template_values.size.height
  }

  /* Calculate height from stored dimensions (for lines/arrows) */
  if (data_type === 'annotation') {
    if (
      template_values?.type?.includes('line') ||
      template_values?.type?.includes('arrow')
    ) {
      /* Use actual line dimensions if available */
      if (template_values?.startPoint && template_values?.endPoint) {
        const dy = Math.abs(
          template_values.endPoint.y - template_values.startPoint.y,
        )
        return Math.max(dy + 50, 100)
      }
    }
  }

  /* Check for height in other common property locations */
  if (template_values?.height) {
    return template_values.height
  }

  /* Type-specific fallback estimates */
  switch (data_type) {
    case 'annotation':
      return 60 // Icons and small annotations

    case 'note':
      return 150 // Notes can be larger

    case 'label':
      return 40

    case 'image':
    case 'view':
      return 300 // Default for images

    case 'video':
      return 400

    case 'table': {
      /* Tables can vary greatly in size */
      const rowCount = template_values?.rows?.length || 5
      return Math.min(rowCount * 40 + 60, 600)
    }

    default:
      return DEFAULT_ELEMENT_HEIGHT
  }
}

type UseVirtualizedElementsProps = {
  templateAttributes: TemplateAttribute<any>[] | undefined
  subPageIds: string[]
  annotationsContainerRef: React.RefObject<HTMLDivElement>
  forceMountedIds?: Set<string> // IDs of elements that must stay mounted (dragging, selected, etc.)
}

export const useVirtualizedElements = ({
  templateAttributes,
  subPageIds,
  annotationsContainerRef,
  forceMountedIds = new Set(),
}: UseVirtualizedElementsProps) => {
  const [viewportInfo, setViewportInfo] = useState({
    scrollTop: 0,
    viewportHeight: 0,
  })
  const [pageOffsetY, setPageOffsetY] = useState(0)

  const virtualizer = useDocumentState((state) => state.virtualizer)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomFactor = useMemo(() => zoom / 100, [zoom])

  // Calculate absolute positions for all elements
  const elementsWithPositions = useMemo<ElementWithPosition[]>(() => {
    if (!templateAttributes || !subPageIds.length) return []

    return templateAttributes.map((template) => {
      const position = template.template_values?.position || { x: 0, y: 0 }
      const subPageId = template.template_values?.subPageId || subPageIds[0]

      // Get Y position relative to the operation page
      const relativeToPageY = getAbsoluteYFromSubPage({
        subPageId,
        relativeY: position.y,
        subPageIds,
      })

      // Add the page offset to get absolute position in the document
      const absoluteY = relativeToPageY * zoomFactor + pageOffsetY

      const estimatedHeight = estimateElementHeight(template)

      return {
        templateAttribute: template,
        absoluteY,
        estimatedHeight: estimatedHeight * zoomFactor,
      }
    })
  }, [templateAttributes, subPageIds, zoomFactor, pageOffsetY])

  // Calculate the offset of the current page in the document
  useEffect(() => {
    if (!virtualizer || !annotationsContainerRef.current) return

    const scrollElement = virtualizer.scrollElement
    if (!scrollElement || !(scrollElement instanceof HTMLElement)) return

    let rafId: number | null = null

    const updatePageOffset = () => {
      const containerRect =
        annotationsContainerRef.current?.getBoundingClientRect()
      const scrollRect = scrollElement.getBoundingClientRect()

      if (containerRect && scrollRect) {
        // Calculate the offset of the container relative to the scroll element
        const offset =
          containerRect.top - scrollRect.top + scrollElement.scrollTop
        setPageOffsetY(offset)
      }
    }

    const debouncedUpdatePageOffset = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        updatePageOffset()
        rafId = null
      })
    }

    // Initial update
    updatePageOffset()

    // Update on scroll and resize (debounced)
    scrollElement.addEventListener('scroll', debouncedUpdatePageOffset, {
      passive: true,
    })
    window.addEventListener('resize', debouncedUpdatePageOffset, {
      passive: true,
    })

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      scrollElement.removeEventListener('scroll', debouncedUpdatePageOffset)
      window.removeEventListener('resize', debouncedUpdatePageOffset)
    }
  }, [virtualizer, annotationsContainerRef])

  // Update viewport info on scroll and resize
  useEffect(() => {
    if (!virtualizer) return

    const scrollElement = virtualizer.scrollElement
    if (!scrollElement || !(scrollElement instanceof HTMLElement)) return

    let rafId: number | null = null

    const updateViewportInfo = () => {
      setViewportInfo({
        scrollTop: scrollElement.scrollTop,
        viewportHeight: scrollElement.clientHeight,
      })
    }

    const debouncedUpdateViewportInfo = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        updateViewportInfo()
        rafId = null
      })
    }

    // Initial update
    updateViewportInfo()

    // Listen to scroll events (debounced)
    scrollElement.addEventListener('scroll', debouncedUpdateViewportInfo, {
      passive: true,
    })
    window.addEventListener('resize', debouncedUpdateViewportInfo, {
      passive: true,
    })

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      scrollElement.removeEventListener('scroll', debouncedUpdateViewportInfo)
      window.removeEventListener('resize', debouncedUpdateViewportInfo)
    }
  }, [virtualizer])

  // Filter to only visible elements (or force-mounted ones)
  const visibleElements = useMemo(() => {
    const { scrollTop, viewportHeight } = viewportInfo

    // If viewport hasn't been initialized yet, render all elements as fallback
    if (viewportHeight === 0) {
      return elementsWithPositions
    }

    const visibleTop = scrollTop - OVERSCAN_PIXELS
    const visibleBottom = scrollTop + viewportHeight + OVERSCAN_PIXELS

    return elementsWithPositions.filter(
      ({ templateAttribute, absoluteY, estimatedHeight }) => {
        // Always mount force-mounted elements (dragging, selected, etc.)
        if (forceMountedIds.has(templateAttribute.id as string)) {
          return true
        }

        // Check if element is in visible range
        const elementTop = absoluteY
        const elementBottom = absoluteY + estimatedHeight

        return elementBottom >= visibleTop && elementTop <= visibleBottom
      },
    )
  }, [elementsWithPositions, viewportInfo, forceMountedIds])

  const stats = useMemo(
    () => ({
      total: elementsWithPositions.length,
      visible: visibleElements.length,
      culled: elementsWithPositions.length - visibleElements.length,
    }),
    [elementsWithPositions.length, visibleElements.length],
  )

  return {
    visibleElements: visibleElements.map((e) => e.templateAttribute),
    stats,
  }
}
